const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

export const MAX_HOURLY_RANGE_DAYS = 7;

export const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
  { key: "all", label: "All time" },
  { key: "custom", label: "Custom" },
];

export const isPresetKey = (value) =>
  PRESETS.some((preset) => preset.key === value);

const HOURLY_PRESETS = ["today", "yesterday"];

export const defaultGranularityForPreset = (presetKey) =>
  HOURLY_PRESETS.includes(presetKey) ? "hour" : "day";

const RELATIVE_PRESET_DAYS = { "7d": 7, "30d": 30, "90d": 90 };

const partsInTimezone = (date, timeZone) =>
  Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
    })
      .formatToParts(date)
      .map((part) => [part.type, part.value]),
  );

export const dayKeyInTimezone = (date, timeZone) => {
  const parts = partsInTimezone(date, timeZone);
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const hourKeyInTimezone = (date, timeZone) => {
  const parts = partsInTimezone(date, timeZone);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}`;
};

export const todayInTimezone = (timeZone, now = new Date()) =>
  dayKeyInTimezone(now, timeZone);

const utcMillisOfDay = (dayKey) => {
  const [year, month, day] = dayKey.split("-").map(Number);
  return Date.UTC(year, month - 1, day);
};

export const shiftDay = (dayKey, days) =>
  new Date(utcMillisOfDay(dayKey) + days * DAY_MS).toISOString().slice(0, 10);

export const rangeDayCount = (dateFrom, dateTo) =>
  Math.round((utcMillisOfDay(dateTo) - utcMillisOfDay(dateFrom)) / DAY_MS) + 1;

export const isValidTimezone = (value) => {
  if (typeof value !== "string" || !value) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch (error) {
    return false;
  }
};

export const isDayKey = (value) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

export const resolvePresetRange = (presetKey, timeZone, firstDay) => {
  const today = todayInTimezone(timeZone);
  if (presetKey === "all") return { dateFrom: firstDay, dateTo: today };
  if (presetKey === "today") return { dateFrom: today, dateTo: today };
  if (presetKey === "yesterday") {
    const yesterday = shiftDay(today, -1);
    return { dateFrom: yesterday, dateTo: yesterday };
  }
  const days = RELATIVE_PRESET_DAYS[presetKey] ?? RELATIVE_PRESET_DAYS["7d"];
  return { dateFrom: shiftDay(today, -(days - 1)), dateTo: today };
};

export const previousRange = (dateFrom, dateTo) => {
  const days = rangeDayCount(dateFrom, dateTo);
  const previousTo = shiftDay(dateFrom, -1);
  return { dateFrom: shiftDay(previousTo, -(days - 1)), dateTo: previousTo };
};

export const buildBucketKeys = ({ from, to, granularity, timeZone }) => {
  if (granularity === "hour") {
    const keys = new Set();
    for (let at = from.getTime(); at <= to.getTime(); at += HOUR_MS) {
      keys.add(hourKeyInTimezone(new Date(at), timeZone));
    }
    keys.add(hourKeyInTimezone(to, timeZone));
    return [...keys];
  }

  const keys = [];
  const lastDay = dayKeyInTimezone(to, timeZone);
  for (
    let day = dayKeyInTimezone(from, timeZone);
    day <= lastDay;
    day = shiftDay(day, 1)
  ) {
    keys.push(day);
  }
  return keys;
};
